import { useInView } from "framer-motion";
import type { BaseHit, Hit } from "instantsearch.js";
import { SendEventForHits } from "instantsearch.js/es/lib/utils/createSendEventForHits";
import React, { useEffect, useRef } from "react";
import { InfiniteHitsProps, useInfiniteHits } from "react-instantsearch";

import styles from "./custom-infinite-hits.module.scss";

type Props = InfiniteHitsProps & {
  hitsCallback?: (hits: Hit<BaseHit>[]) => void;
  hitComponent?: React.JSXElementConstructor<{
    hit: Hit<BaseHit>;
    sendEvent: SendEventForHits;
    index?: number;
  }>;
};

export default function CustomInfiniteHits(props: Props) {
  const { hitsCallback: _, ...infiniteHitsProps } = props;
  const {
    items: hits,
    isLastPage,
    showMore,
    sendEvent,
    showPrevious,
    isFirstPage,
  } = useInfiniteHits(infiniteHitsProps);
  const sentinelRef = useRef(null);
  const sentinelRef2 = useRef(null);
  const sentinelInView = useInView(sentinelRef, { margin: "0% 0% 50% 0%" });
  const sentinel2InView = useInView(sentinelRef2, { margin: "0% 0% -50% 0%" });

  // handle infinite scroll - show next page
  useEffect(() => {
    if (sentinelInView && !isLastPage) {
      showMore();
    }
  }, [isLastPage, sentinelInView, showMore]);

  // handle infinite scroll - show previous page
  useEffect(() => {
    if (sentinel2InView && !isFirstPage) {
      showPrevious();
    }
  }, [isFirstPage, sentinel2InView, showPrevious]);

  useEffect(() => {
    if (props.hitsCallback) {
      props.hitsCallback(hits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hits.length]);

  return (
    <div className={styles.hitsWrapper}>
      <div ref={sentinelRef2} aria-hidden="true" />
      <ul className={styles.grid}>
        {hits.map((hit, idx) => (
          <li key={hit.objectID} className={styles.tile}>
            {props.hitComponent != undefined && <props.hitComponent hit={hit} sendEvent={sendEvent} index={idx} />}
          </li>
        ))}
      </ul>
      <div ref={sentinelRef} aria-hidden="true" />
    </div>
  );
}
